<template>
  <v-form action="javascript:void(0)" method="post" ref="form" id="contact-form">
    <v-container class="pa-0">
      <v-layout row class="info-pill">
        <v-flex d-flex pr-1><v-icon>info</v-icon></v-flex>
        <v-flex d-flex>{{$tf('web support warning')}}</v-flex>
      </v-layout>
      <v-layout wrap>
          <v-flex xs12 sm6 md4>
            <v-text-field
                ref="firstname"
                name="firstname"
                :label="$tf('Firstname')"
                required
                v-model="fields.firstname.value"
                :rules="fields.firstname.rules"
            ></v-text-field>
          </v-flex>
          <v-flex xs12 sm6 md4>
            <v-text-field
                name="lastname"
                :label="$tf('Lastname')"
                required
                v-model="fields.lastname.value"
                :rules="fields.lastname.rules"
            ></v-text-field>
          </v-flex>
          <v-flex xs12 sm6>
            <v-text-field
                name="email"
                label="Email"
                required
                v-model="fields.email.value"
                id="email"
                :rules="fields.email.rules"
            ></v-text-field>
          </v-flex>
          <v-flex xs12>
            <v-text-field
                name="message"
                :label="$tf('Your message')"
                required
                v-model="fields.message.value"
                multi-line
                :rules="fields.message.rules"
              ></v-text-field>
          </v-flex>

          <v-flex xs12 class="recaptcha-container mt-3 mb-3">
              <div id="recaptcha-contact-form"></div>
              <div v-if="captchaError" class="v-messages theme--light error--text" role="alert">
                 <div class="v-messages__wrapper">
                   <div class="v-messages__message">{{ captchaError }}</div>
                 </div>
               </div>
          </v-flex>

        </v-layout>
    </v-container>
  </v-form>
</template>

<script>
  import api from '~/store/idonia-api/api'
  import useInPlaceValidation from '~/commons/validation'
  import { ref } from '@vue/composition-api'

  window.renderContactCaptchaCallback = () => {
    const instance = window.contactFormInstance
    if (instance && typeof instance.renderCaptcha === 'function') {
      instance.renderCaptcha()
    } else {
      console.error('Contact form instance or renderCaptcha method not found for CAPTCHA callback.')
    }
  }

  export default {
    name: 'contact-form',
    props: {
      submit: { type: Boolean, default: false },
      reset: { type: Boolean, default: false },
      loading: { type: Boolean, default: false },
      account: { type: Object, default: () => ({}) }
    },
    setup (props) {
      const { fields, setValidationCallback } = useInPlaceValidation({
        firstname: { name: 'Firstname', rules: ['required', 'maxLength:100'], value: ref(props.account.firstname || '') },
        lastname: { name: 'Lastname', rules: ['required', 'maxLength:100'], value: ref(props.account.lastname || '') },
        email: { name: 'Email', rules: ['required', 'email'], value: ref(props.account.email || '') },
        message: { name: 'Your message', rules: ['required'], value: ref('') }
      })

      return {
        fields,
        setValidationCallback
      }
    },
    data () {
      return {
        recaptchaKey: (this.$config && this.$config.recaptchaKey) || '',
        isCaptchaRendered: false,
        captchaError: ''
      }
    },
    mounted () {
      this.setValidationCallback(this.setValid)

      window.contactFormInstance = this

      if (this.recaptchaKey.length > 0) {
        if (typeof window.grecaptcha === 'undefined') {
          const recaptchaScript = document.createElement('script')
          const lang = this.$i18n && this.$i18n.locale ? this.$i18n.locale.split('-')[0] : 'en'
          recaptchaScript.setAttribute('src', `https://www.google.com/recaptcha/api.js?onload=renderContactCaptchaCallback&render=explicit&hl=${lang}`)
          recaptchaScript.setAttribute('defer', 'true')
          recaptchaScript.setAttribute('async', 'true')
          document.head.appendChild(recaptchaScript)
          console.log('reCAPTCHA script appended to head.')
        } else {
          console.log('reCAPTCHA script already loaded, attempting render.')
          this.renderCaptcha()
        }
      } else {
        console.warn('reCAPTCHA site key (`recaptchaKey`) is missing in config. CAPTCHA will not be displayed.')
      }
    },
    beforeDestroy () {
      if (window.contactFormInstance === this) {
        delete window.contactFormInstance
      }
    },
    methods: {
      setValid (valid) {
        this.$store.dispatch('dialog/setValid', valid)
      },

      cancel () {
        console.log('Cancel clicked in contact form')
        this.resetFormAndCaptcha()
        this.$emit('cancel')
      },

      async renderCaptcha () {
        if (!this.isCaptchaRendered && this.recaptchaKey.length > 0 && typeof window.grecaptcha !== 'undefined' && window.grecaptcha.render) {
          try {
            const baseUrl = this.getBaseApiUrl()
            if (!baseUrl) return

            const statusEndpoint = baseUrl + 'captcha-status/send-contact-form'
            const response = await this.$http.get(statusEndpoint)

            const shouldRenderCaptcha = response && (response.data === true || response.body === true || (typeof response.data === 'object' && response.data && response.data.status === true))

            if (shouldRenderCaptcha) {
              const containerId = 'recaptcha-contact-form'
              const container = document.getElementById(containerId)

              if (container && !container.hasChildNodes()) {
                console.log('Rendering reCAPTCHA widget.')
                window.grecaptcha.render(containerId, {
                  'sitekey': this.recaptchaKey,
                  'hl': this.$i18n && this.$i18n.locale ? this.$i18n.locale.split('-')[0] : 'en'
                })
                this.isCaptchaRendered = true
                this.captchaError = ''
              } else if (!container) {
                console.error(`reCAPTCHA container with ID "${containerId}" not found.`)
              } else {
                console.log('reCAPTCHA container already has content, skipping render.')
              }
            } else {
              console.log('CAPTCHA not required for contact form based on API status.')
            }
          } catch (error) {
            console.error('Error checking CAPTCHA status or rendering CAPTCHA:', error)
          }
        } else if (this.isCaptchaRendered) {
          console.log('CAPTCHA already rendered.')
        } else {
          console.log('CAPTCHA prerequisites not met for rendering.')
        }
      },

      resetCaptcha () {
        if (this.isCaptchaRendered && window.grecaptcha) {
          try {
            window.grecaptcha.reset()
            console.log('reCAPTCHA widget reset.')
          } catch (error) {
            console.error('Error resetting reCAPTCHA widget:', error)
          }
        }
        this.captchaError = ''
      },

      resetFormAndCaptcha () {
        if (this.$refs.form && typeof this.$refs.form.reset === 'function') {
          this.$refs.form.reset()
          this.fields.firstname.value = this.account.firstname || ''
          this.fields.lastname.value = this.account.lastname || ''
          this.fields.email.value = this.account.email || ''
          this.fields.message.value = ''
        }
        this.resetCaptcha()
        this.$nextTick(() => {
          if (this.$refs.form && typeof this.$refs.form.resetValidation === 'function') {
            this.$refs.form.resetValidation()
          }
          this.setValid(false)
        })
      },

      getBaseApiUrl () {
        let baseUrl = ''
        if (api && api.config && typeof api.config.apiUrl === 'string' && api.config.apiUrl.trim() !== '') {
          baseUrl = api.config.apiUrl
        } else {
          console.error('API Base URL is not configured or available in api.config.apiUrl!')
          this.$store.dispatch('feedback/addSnack', { text: this.$tf('Configuration error: Cannot determine API server.'), type: 'error', timeout: 5000 })
          return null
        }
        if (!baseUrl.endsWith('/')) {
          baseUrl += '/'
        }
        return baseUrl
      },

      accept () {
        this.$emit('update:loading', true)

        // 1. Validate Form Fields (Vuetify validation)
        if (!this.$refs.form.validate()) {
          this.$store.dispatch('dialog/setValid', false)
          console.log('Form validation failed.')
          this.$emit('update:loading', false)
          return Promise.reject(new Error('Form is not valid'))
        }
        this.$store.dispatch('dialog/setValid', true)

        // 2. Validate CAPTCHA if rendered
        let challenge = ''
        this.captchaError = ''
        if (this.isCaptchaRendered) {
          challenge = window.grecaptcha.getResponse()
          if (challenge.length === 0) {
            this.captchaError = this.$tf('Please complete the CAPTCHA verification.')
            this.$store.dispatch('dialog/setValid', false)
            console.log('CAPTCHA validation failed: User did not complete.')
            this.$emit('update:loading', false)
            return Promise.reject(new Error('Captcha verification failed'))
          }
        } else {
          console.log('CAPTCHA not rendered, proceeding without challenge.')
        }

        // 3. Construct Payload
        const payload = {
          type: 'SALES',
          name: this.fields.firstname.value,
          family_name: this.fields.lastname.value,
          email: this.fields.email.value,
          message: this.fields.message.value,
          challenge: challenge // Add the CAPTCHA token
        }

        // 4. Get API Endpoint URL
        const baseUrl = this.getBaseApiUrl()
        if (!baseUrl) {
          this.$store.dispatch('dialog/setValid', false)
          this.$emit('update:loading', false)
          return Promise.reject(new Error('API Configuration error'))
        }
        const endpoint = baseUrl + 'notifications/send-contact-form'

        // 5. Send Request via $http.post
        return this.$http.post(endpoint, payload)
          .then((res) => {
            const responseStatus = (res && res.status) ? res.status : 200
            this.showSendindRespone(responseStatus)

            this.$store.dispatch('feedback/addSnack', {
              text: this.$tf(`Thank you, your message has been sent`),
              type: 'info',
              timeout: 5000
            })

            this.resetFormAndCaptcha()
            return res
          })
          .catch(err => {
            const errorStatus = (err && err.response && err.response.status) ? err.response.status : 500
            this.showSendindRespone(errorStatus)

            console.error('Error sending contact form:', err)

            if ((errorStatus === 400 || errorStatus === 403) && this.isCaptchaRendered) {
              this.captchaError = this.$tf('CAPTCHA verification failed. Please try again.')
            }

            this.$store.dispatch('feedback/addSnack', {
              text: this.$tf(`Error sending message. Please try again later.`),
              type: 'error',
              timeout: 5000
            })

            this.resetFormAndCaptcha()
            this.$store.dispatch('dialog/setValid', false)
            throw err
          })
          .finally(() => {
            this.$emit('update:loading', false)
          })
      },

      showSendindRespone (status) {
        if (status < 200 || status >= 300) {
          console.warn(`Contact form submission responded with status: ${status}`)
        }
      }
    }
  }
</script>

<style>
  #contact-form .v-input--is-required .v-label::after,
  #contact-form .v-input--checkbox.v-input--is-required .v-label::after {
    content: "*";
    padding-left: 2px;
    color: red;
    font-weight: bold;
    vertical-align: super;
    font-size: 0.8em;
  }
</style>

<style lang="scss" scoped>
 .recaptcha-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 78px;
    width: 100%;
    margin-top: 16px;
 }

 .v-messages.error--text {
     margin-top: 4px;
     text-align: center;
     width: 100%;
     padding-left: 12px;
     padding-right: 12px;
 }

 /deep/ .v-input--checkbox .v-label a {
     pointer-events: auto;
     text-decoration: underline;
     &:hover {
        color: var(--v-anchor-base);
     }
 }
</style>



// WEBPACK FOOTER //
// src/components/common/dialog/types/DialogContactForm.vue